import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Bouton « partager » + menu de partage (LinkedIn, Facebook, X, WhatsApp, Instagram, e-mail, lien).
//
// Instagram n'accepte pas de lien envoyé depuis un site web : sur téléphone, on ouvre le menu de
// partage du téléphone (Instagram y figure : story, message) ; sur ordinateur, le lien est copié.

const T = {
  fr: {
    share: 'partager', title: 'Partager', close: 'Fermer', copy: 'Copier le lien', copied: 'Lien copié ✓',
    email: 'E-mail', more: 'Autres applis…', copyFail: 'Copie impossible : sélectionnez le lien ci-dessous',
    insta: 'Lien copié ✓ — collez-le dans Instagram (sticker « lien » d\'une story, ou votre bio).',
    hint: 'LinkedIn et Facebook affichent automatiquement le titre, le résumé et l\'image de la page.',
  },
  en: {
    share: 'share', title: 'Share', close: 'Close', copy: 'Copy link', copied: 'Link copied ✓',
    email: 'Email', more: 'More apps…', copyFail: 'Could not copy: select the link below',
    insta: 'Link copied ✓ — paste it in Instagram (link sticker in a story, or your bio).',
    hint: 'LinkedIn and Facebook automatically show the page title, summary and image.',
  },
};

const enc = encodeURIComponent;
const isTouch = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch {}
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

const Glyph = {
  share: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12M12 3l-4.5 4.5M12 3l4.5 4.5M5 12v6.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  chat: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 19.5l1.2-3.6A7.5 7.5 0 1 1 8.6 18.6z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /><path d="M9.5 9.5c.3 1.9 2.1 3.7 4 4l1-1 1.6.8c-.2 1-1 1.7-2 1.7-3 0-6.1-3.1-6.1-6.1 0-1 .7-1.8 1.7-2l.8 1.6z" fill="currentColor" /></svg>,
  camera: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="3.5" width="17" height="17" rx="5" fill="none" stroke="currentColor" strokeWidth="2" /><circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" /><circle cx="17" cy="7" r="1.2" fill="currentColor" /></svg>,
  mail: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5.5" width="18" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M3.5 7l8.5 6 8.5-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>,
  link: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>,
  more: <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5.5" cy="12" r="2" fill="currentColor" /><circle cx="12" cy="12" r="2" fill="currentColor" /><circle cx="18.5" cy="12" r="2" fill="currentColor" /></svg>,
  close: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>,
};

function ShareSheet({ url, title, text, lang, onClose }) {
  const t = T[lang] || T.fr;
  const [toast, setToast] = useState('');
  const [showUrl, setShowUrl] = useState(false);
  const firstRef = useRef(null);
  const toastTimer = useRef(null);
  const message = text || title || '';
  const canNative = typeof navigator !== 'undefined' && !!navigator.share;

  useEffect(() => {
    const prevFocus = document.activeElement;
    firstRef.current && firstRef.current.focus();
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => { clearTimeout(toastTimer.current); window.removeEventListener('keydown', onKey, true); if (prevFocus && prevFocus.focus) prevFocus.focus(); };
  }, [onClose]);

  const flash = (msg, ms = 2200) => { setToast(msg); clearTimeout(toastTimer.current); toastTimer.current = setTimeout(() => setToast(''), ms); };
  const open = (href) => { window.open(href, '_blank', 'noopener,noreferrer,width=640,height=700'); };
  const native = async () => {
    try { await navigator.share({ title, text: message, url }); onClose(); }
    catch (e) { if (!e || e.name !== 'AbortError') flash(t.copyFail); }
  };
  const copy = async (msg = t.copied, ms) => {
    if (await copyText(url)) flash(msg, ms); else { setShowUrl(true); flash(t.copyFail); }
  };

  const targets = [
    { key: 'linkedin', label: 'LinkedIn', mark: 'in', cls: 'sh-linkedin', action: () => open(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`) },
    { key: 'facebook', label: 'Facebook', mark: 'f', cls: 'sh-facebook', action: () => open(`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`) },
    { key: 'x', label: 'X', mark: 'X', cls: 'sh-x', action: () => open(`https://x.com/intent/post?text=${enc(message)}&url=${enc(url)}`) },
    { key: 'whatsapp', label: 'WhatsApp', icon: Glyph.chat, cls: 'sh-whatsapp', action: () => open(`https://wa.me/?text=${enc(`${message} ${url}`.trim())}`) },
    { key: 'instagram', label: 'Instagram', icon: Glyph.camera, cls: 'sh-instagram', action: () => (canNative && isTouch() ? native() : copy(t.insta, 5000)) },
    { key: 'email', label: t.email, icon: Glyph.mail, cls: 'sh-email', action: () => { window.location.href = `mailto:?subject=${enc(title || '')}&body=${enc(`${message}\n\n${url}`)}`; } },
    { key: 'copy', label: t.copy, icon: Glyph.link, cls: 'sh-copy', action: () => copy() },
    ...(canNative ? [{ key: 'more', label: t.more, icon: Glyph.more, cls: 'sh-more', action: native }] : []),
  ];

  return createPortal(
    <div className="sh-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sh-sheet" role="dialog" aria-modal="true" aria-labelledby="sh-title" lang={lang}>
        <div className="sh-grab" aria-hidden="true" />
        <div className="sh-head">
          <div className="sh-head-text">
            <h2 id="sh-title">{t.title}</h2>
            {title && <p className="sh-subject">{title}</p>}
          </div>
          <button type="button" className="sh-close" onClick={onClose} aria-label={t.close}>{Glyph.close}</button>
        </div>
        <div className="sh-grid">
          {targets.map((x, i) => (
            <button key={x.key} ref={i === 0 ? firstRef : null} type="button" className={`sh-target ${x.cls}`} onClick={x.action}>
              <span className="sh-icon" aria-hidden="true">{x.icon || <b>{x.mark}</b>}</span>
              <span className="sh-label">{x.label}</span>
            </button>
          ))}
        </div>
        {showUrl && <input className="sh-url" readOnly value={url} onFocus={e => e.target.select()} autoFocus />}
        <p className="sh-hint">{t.hint}</p>
        {toast && <div className="sh-toast" role="status">{toast}</div>}
      </div>
    </div>,
    document.body
  );
}

// variant : 'pill' (bouton arrondi), 'icon' (rond, icône seule), 'link' (texte souligné),
// ou n'importe quelle className via `className` (ex. boutons du lecteur QCM)
export default function ShareButton({ url, title, text, lang = 'fr', variant = 'pill', label, className = '', children }) {
  const [open, setOpen] = useState(false);
  const t = T[lang] || T.fr;
  const shareUrl = () => {
    if (url) return /^https?:/.test(url) ? url : window.location.origin + url;
    return window.location.href;
  };
  const [frozenUrl, setFrozenUrl] = useState('');
  const show = () => { setFrozenUrl(shareUrl()); setOpen(true); };

  return (
    <>
      <button type="button" className={`share-btn share-${variant} ${className}`} onClick={show}
        aria-haspopup="dialog" aria-label={variant === 'icon' ? (label || t.share) : undefined}>
        {children || (
          <>
            <span className="share-ico" aria-hidden="true">{Glyph.share}</span>
            {variant !== 'icon' && <span>{label || t.share}</span>}
          </>
        )}
      </button>
      {open && <ShareSheet url={frozenUrl} title={title || document.title} text={text} lang={lang} onClose={() => setOpen(false)} />}
    </>
  );
}

export { ShareSheet };
